import { motion } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';

import { ACCENT_BLUE, ACCENT_CYAN, DARK_BG, MUTED_GRAY, NOPING_GREEN } from '../constants';
import { Reveal } from '../ui-helpers';
import { JitteryBars } from './jittery-bars';
import { SmoothLine } from './smooth-line';

function useFluctuatingValue(min: number, max: number, intervalMs: number, decimals = 0) {
  const [value, setValue] = useState(Math.round((min + max) / 2));

  useEffect(() => {
    const tick = () => {
      const raw = min + Math.random() * (max - min);
      setValue(decimals > 0 ? parseFloat(raw.toFixed(decimals)) : Math.round(raw));
    };
    tick();
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [min, max, intervalMs, decimals]);

  return value;
}

function AnimatedGauge({ label, value, max, unit, barColor, labelColor, valueColor }: { label: string; value: number; max: number; unit: string; barColor: string; labelColor: string; valueColor: string }) {
  const widthPct = Math.max((value / max) * 100, 2);

  return (
    <div>
      <div className='mb-1 flex justify-between font-mono text-xs'>
        <span className='uppercase' style={{ color: labelColor }}>
          {label}
        </span>
        <span className='font-bold' style={{ color: valueColor }}>
          {value}
          {unit}
        </span>
      </div>
      <div className='h-2 overflow-hidden rounded-full bg-white/5'>
        <motion.div className='h-full rounded-full' style={{ backgroundColor: barColor }} animate={{ width: `${widthPct}%` }} transition={{ duration: 0.5, ease: 'easeInOut' }} />
      </div>
    </div>
  );
}

export function BeforeAfterSection() {
  const { t } = useTranslation(['components/trial/lp/v1b']);

  const beforePing = useFluctuatingValue(120, 280, 900);
  const beforePacketLoss = useFluctuatingValue(5, 30, 900);
  const beforeJitter = useFluctuatingValue(20, 80, 900);

  const afterPing = useFluctuatingValue(1, 5, 1200);
  const afterJitter = useFluctuatingValue(0.5, 2, 1200, 1);

  return (
    <>
      <div className='relative grid gap-0 md:grid-cols-2'>
        <div className='absolute inset-y-0 left-1/2 z-20 hidden -translate-x-1/2 items-center md:flex'>
          <div className='flex size-12 items-center justify-center rounded-full' style={{ backgroundColor: DARK_BG, border: `2px solid ${NOPING_GREEN}40` }}>
            <svg className='size-5' fill='none' viewBox='0 0 24 24' stroke={NOPING_GREEN} strokeWidth={2}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M13 7l5 5m0 0l-5 5m5-5H6' />
            </svg>
          </div>
        </div>

        <Reveal>
          <div className='h-full rounded-2xl p-6 md:rounded-e-none md:p-8' style={{ border: `1px solid ${ACCENT_BLUE}33`, backgroundColor: `${ACCENT_BLUE}0D` }}>
            <div className='mb-6 flex items-center justify-between'>
              <div>
                <p className='font-mono text-sm font-semibold uppercase tracking-wider' style={{ color: ACCENT_BLUE }}>
                  {t('before_after.standard_connection')}
                </p>
                <p className='mt-1 font-mono text-4xl font-black md:text-5xl' style={{ color: ACCENT_BLUE }}>
                  {beforePing}
                  <span className='text-lg font-normal opacity-60'>ms</span>
                </p>
              </div>
              <div className='flex items-center gap-2'>
                <span className='font-mono text-[10px] uppercase' style={{ color: `${ACCENT_BLUE}99` }}>
                  {t('before_after.unstable')}
                </span>
                <div className='size-3 animate-pulse rounded-full' style={{ backgroundColor: ACCENT_BLUE }} />
              </div>
            </div>
            <JitteryBars />
            <div className='mt-6 space-y-3'>
              <AnimatedGauge label='Ping' value={beforePing} max={300} unit='ms' barColor={ACCENT_BLUE} labelColor={`${ACCENT_BLUE}B3`} valueColor={ACCENT_BLUE} />
              <AnimatedGauge label='Packet Loss' value={beforePacketLoss} max={100} unit='%' barColor={ACCENT_BLUE} labelColor={`${ACCENT_BLUE}B3`} valueColor={ACCENT_BLUE} />
              <AnimatedGauge label='Jitter' value={beforeJitter} max={100} unit='ms' barColor={ACCENT_BLUE} labelColor={`${ACCENT_BLUE}B3`} valueColor={ACCENT_BLUE} />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className='h-full rounded-2xl p-6 md:rounded-s-none md:p-8' style={{ border: `1px solid ${ACCENT_CYAN}20`, backgroundColor: `${ACCENT_CYAN}05` }}>
            <div className='mb-6 flex items-center justify-between'>
              <div>
                <p className='font-mono text-sm font-semibold uppercase tracking-wider' style={{ color: ACCENT_CYAN }}>
                  {t('before_after.noping_optimized')}
                </p>
                <p className='mt-1 font-mono text-4xl font-black md:text-5xl' style={{ color: ACCENT_CYAN }}>
                  {afterPing}
                  <span className='text-lg font-normal opacity-60'>ms</span>
                </p>
              </div>
              <div className='flex items-center gap-2'>
                <span className='font-mono text-[10px] uppercase' style={{ color: `${NOPING_GREEN}CC` }}>
                  {t('before_after.optimal')}
                </span>
                <div className='size-3 animate-pulse rounded-full' style={{ backgroundColor: NOPING_GREEN }} />
              </div>
            </div>
            <SmoothLine />
            <div className='mt-6 space-y-3'>
              <AnimatedGauge label='Ping' value={afterPing} max={200} unit='ms' barColor={NOPING_GREEN} labelColor={`${ACCENT_CYAN}B0`} valueColor={ACCENT_CYAN} />
              <AnimatedGauge label='Packet Loss' value={0} max={100} unit='%' barColor={NOPING_GREEN} labelColor={`${ACCENT_CYAN}B0`} valueColor={ACCENT_CYAN} />
              <AnimatedGauge label='Jitter' value={afterJitter} max={60} unit='ms' barColor={ACCENT_CYAN} labelColor={`${ACCENT_CYAN}B0`} valueColor={ACCENT_CYAN} />
            </div>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.4} className='mt-8 flex justify-center'>
        <div className='inline-flex items-center gap-3 rounded-full px-6 py-3' style={{ border: `1px solid ${NOPING_GREEN}30`, backgroundColor: `${NOPING_GREEN}05` }}>
          <span className='font-mono text-3xl font-black' style={{ color: NOPING_GREEN }}>
            {t('before_after.reduction_value')}
          </span>
          <span className='text-sm' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
            {t('before_after.reduction_label')}
          </span>
        </div>
      </Reveal>
    </>
  );
}
